import { LucideIcon, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface RooferTypeCardProps {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  ctaText?: string;
  ctaLink?: string;
  color?: 'purple' | 'amber' | 'blue' | 'green' | 'orange' | 'cyan';
  className?: string;
}

const slateStyle = {
  gradient: 'from-slate-800 to-slate-900 border border-slate-700/80',
  bg: 'bg-slate-800/80',
  border: 'border-slate-800 hover:border-slate-700',
  icon: 'text-slate-300',
  check: 'text-slate-300',
};

const emeraldStyle = {
  ...slateStyle,
  icon: 'text-emerald-400',
  check: 'text-emerald-400',
};

export function RooferTypeCard({
  icon: Icon,
  title,
  subtitle,
  description,
  features,
  ctaText = 'Learn More',
  ctaLink = '#',
  color = 'purple',
  className,
}: RooferTypeCardProps) {
  const colors = color === 'green' ? emeraldStyle : slateStyle;

  return (
    <div
      className={cn(
        'group relative bg-white/[0.03] backdrop-blur-xl rounded-2xl p-8 border transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.5)]',
        colors.border,
        className
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'w-14 h-14 rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br shadow-inner',
          colors.gradient
        )}
      >
        <Icon className="w-7 h-7 text-white" />
      </div>

      {/* Title */}
      <div className="mb-4">
        <p className={cn('text-sm font-semibold mb-1', colors.icon)}>{subtitle}</p>
        <h3 className="text-2xl font-bold text-white">{title}</h3>
      </div>

      {/* Description */}
      <p className="text-white/90 mb-6">{description}</p>

      {/* Features */}
      <ul className="space-y-3 mb-8">
        {features.map((feature, i) => (
          <li key={i} className="flex items-center gap-3 text-white/80 text-sm">
            <CheckCircle className={cn('w-5 h-5 flex-shrink-0', colors.check)} />
            {feature}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <Link to={ctaLink}>
        <Button
          variant="outline"
          className={cn(
            'w-full border-white/20 text-white hover:bg-white/10',
            'group-hover:border-white/40'
          )}
        >
          {ctaText}
          <ArrowRight className="ml-2 size-4" />
        </Button>
      </Link>
    </div>
  );
}

export default RooferTypeCard;
